import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDrop } from "react-dnd";
import { Dropdown, Form } from "react-bootstrap";
import { TableColumn } from "../table-view";
import { FormattedMessage, useIntl } from "react-intl";
import TooltipTrigger from "../../tooltip-trigger";
import "./column-tools-view.scss";
import { DragItem } from "../header-cell/header-cell-view";
import { useId } from "react";

//<editor-fold desc="Props">
export interface ColumnToolsVisibilityProps {
  show?: boolean;
  onShow: () => void;
  onHide: () => void;
}
export interface ColumnToolsProps extends ColumnToolsVisibilityProps {
  columns: TableColumn[];
  onToggleColumn?: (key: string, visible: boolean) => void;
}
//</editor-fold>

export default function ColumnTools(props: ColumnToolsProps) {
  //<editor-fold desc="Local variables">
  const intl = useIntl();
  const { columns, onToggleColumn, show, onShow, onHide } = props;
  const cols = columns.filter((c: TableColumn) => !c.isTechnical);
  const visibleCols = cols.filter((c: TableColumn) => !c.hidden);
  //</editor-fold>

  //<editor-fold desc="Hooks">
  const idPrefix = useId();
  //</editor-fold>
  const onToggleHandler = (isOpen: boolean, event: any, metadata: any) => {
    if (show) {
      if (metadata.source !== "select") {
        onHide();
      }
    } else {
      onShow();
    }
  };
  const [{ isOver }, drop] = useDrop({
    accept: "column",
    drop: (item: DragItem) => {
      if (onToggleColumn && visibleCols.length > 1) {
        onToggleColumn(item.id, false);
      }
      return { ...item, hide: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  return (
    <>
      <Dropdown
        className={"btn-group column-tools"}
        show={show}
        onToggle={onToggleHandler}
      >
        <TooltipTrigger
          tipKey={"toolsToggleColumnTip"}
          tip={intl.formatMessage({
            defaultMessage: "Select columns to display.",
            description: "tooltip for the select columns button",
          })}
        >
          <Dropdown.Toggle
            variant={"theme-to-primary"}
            data-test-table-columns-trigger
          >
            <div
              ref={drop}
              className={
                isOver && visibleCols.length > 1
                  ? "column-tools-drop is-drag-over is-droppable"
                  : "column-tools-drop"
              }
            >
              {
                <span className="fa-layers fa-fw">
                  <FontAwesomeIcon
                    icon={"pen"}
                    mask="columns"
                    className={"opacity-50"}
                    transform="grow-2 right-8 down-3"
                  />
                  <FontAwesomeIcon
                    icon={"pen"}
                    transform="shrink-5 right-6 down-5"
                  />
                </span>
              }
            </div>
          </Dropdown.Toggle>
        </TooltipTrigger>

        <Dropdown.Menu
          data-test-table-columns
          popperConfig={{
            modifiers: [
              {
                name: "computeStyles",
                options: {
                  gpuAcceleration: false, // true by default. Without this random conf, unnecessary scrollbars are added to parents
                },
              },
            ],
          }}
        >
          <Dropdown.Header className={"border-bottom"}>
            <FormattedMessage
              defaultMessage="Toggle columns"
              description={"Heading for column selection dropdown"}
            />
          </Dropdown.Header>
          {cols &&
            cols.map((c: TableColumn) => {
              return (
                <TooltipTrigger
                  key={c.key}
                  tipKey={c.key + "LastVisibleTip"}
                  tip={
                    visibleCols.length < 2 && !c.hidden
                      ? intl.formatMessage({
                          defaultMessage:
                            "The last column cannot be removed as a minimum of 1 column must be active at all times.",
                          description:
                            "tooltip for disabled last column remove",
                        })
                      : undefined
                  }
                >
                  <Dropdown.Item
                    data-test-table-column={c.key}
                    onClick={
                      visibleCols.length > 1 || !!c.hidden
                        ? (e: any) => {
                            e.preventDefault();
                            if (onToggleColumn) {
                              onToggleColumn(c.key, !!c.hidden);
                            }
                          }
                        : undefined
                    }
                  >
                    <Form.Check
                      id={idPrefix + c.key}
                      readOnly
                      disabled={visibleCols.length < 2 && !c.hidden}
                      custom
                      checked={!c.hidden}
                      type={"checkbox"}
                      label={c.label}
                    />
                  </Dropdown.Item>
                </TooltipTrigger>
              );
            })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
}
